import type { RegisterImplementationProps } from 'constants/types';
import MultiSignInBase, { type MultiSignInStep } from '../MultiSignInBase';
import { getProviderFormLabel } from '../utils';
import Welcome from './MultiSignIn/Welcome';
import TenantId from './MultiSignIn/TenantId';
import CreateIntegrationSystemUser from './MultiSignIn/CreateIntegrationSystemUser';
import ConfigureSecurityGroup from './MultiSignIn/ConfigureSecurityGroup';
import ConfigureDomainPermissions from './MultiSignIn/ConfigureDomainPermissions';
import ActivateSecurityPolicy from './MultiSignIn/ActivateSecurityPolicy';
import ConfigureAuthPolicy from './MultiSignIn/ConfigureAuthPolicy';
import ActivateAuthPolicy from './MultiSignIn/ActivateAuthPolicy';
import RetrieveWebServicesUrl from './MultiSignIn/RetrieveWebServicesUrl';

const SignInWorkdayApiMultiStep = ({
  client,
  isLoading,
  onSubmit,
  provider,
  instructionsLink,
  instructionsInnerHtmlToScroll,
  instructionsInnerHtmlToScrollStep2,
  instructionsInnerHtmlToScrollStep3,
  instructionsInnerHtmlToScrollStep4,
  instructionsInnerHtmlToScrollStep5,
  instructionsInnerHtmlToScrollStep6,
  instructionsInnerHtmlToScrollStep7,
  instructionsInnerHtmlToScrollStep8,
}: RegisterImplementationProps) => {
  const steps: MultiSignInStep[] = [
    {
      id: 'welcome',
      title: `Let's create your connection`,
      fieldNames: [],
      welcomePage: true,
      spacing: 'md',
    },
    {
      id: 'tenantId',
      title: 'Retrieve the Workday Tenant ID',
      fieldNames: ['tenantId'],
      spacing: 'sm',
    },
    {
      id: 'createUser',
      title: 'Create an Integration System User',
      fieldNames: ['username', 'password'],
      spacing: 'sm',
    },
    {
      id: 'securityGroup',
      title: 'Configure a Security Group',
      fieldNames: ['securityGroupConfirmed'],
      spacing: 'sm',
    },
    {
      id: 'domainPermissions',
      title: 'Configure the Domain Security Policy Permissions',
      fieldNames: ['domainPermissionsConfirmed'],
      spacing: 'sm',
    },
    {
      id: 'activateSecurity',
      title: 'Activate the Security Policy Changes',
      fieldNames: ['activateSecurityConfirmed'],
      spacing: 'sm',
    },
    {
      id: 'authPolicy',
      title: 'Configure Authentication Policy',
      fieldNames: ['authPolicyConfirmed'],
      spacing: 'sm',
    },
    {
      id: 'activateAuth',
      title: 'Activate Authentication Policy Changes',
      fieldNames: ['activateAuthConfirmed'],
      spacing: 'sm',
    },
    {
      id: 'webServices',
      title: 'Retrieve your Web Services Endpoint URL',
      fieldNames: ['apiBaseUrl'],
      spacing: 'sm',
    },
  ];

  return (
    <MultiSignInBase
      formLabel={getProviderFormLabel(provider)}
      steps={steps}
      onSubmit={onSubmit}
      isLoading={isLoading}
      stepCounterLabel="Part"
      render={(fields, currentStep) => {
        // Step 0: Welcome page
        if (currentStep === 0) {
          return <Welcome client={client} provider={provider} />;
        }

        // Step 1: Tenant ID
        if (currentStep === 1) {
          return (
            <TenantId
              tenantId={fields.tenantId}
              instructionsLink={instructionsLink}
              instructionsInnerHtmlToScroll={instructionsInnerHtmlToScroll}
            />
          );
        }

        // Step 2: Create Integration System User
        if (currentStep === 2) {
          return (
            <CreateIntegrationSystemUser
              username={fields.username}
              password={fields.password}
              instructionsLink={instructionsLink}
              instructionsInnerHtmlToScroll={instructionsInnerHtmlToScrollStep2}
            />
          );
        }

        // Step 3: Configure Security Group
        if (currentStep === 3) {
          return (
            <ConfigureSecurityGroup
              securityGroupConfirmed={fields.securityGroupConfirmed}
              instructionsLink={instructionsLink}
              instructionsInnerHtmlToScroll={instructionsInnerHtmlToScrollStep3}
            />
          );
        }

        // Step 4: Configure Domain Permissions
        if (currentStep === 4) {
          return (
            <ConfigureDomainPermissions
              domainPermissionsConfirmed={fields.domainPermissionsConfirmed}
              instructionsLink={instructionsLink}
              instructionsInnerHtmlToScroll={instructionsInnerHtmlToScrollStep4}
            />
          );
        }

        // Step 5: Activate Security Policy
        if (currentStep === 5) {
          return (
            <ActivateSecurityPolicy
              activateSecurityConfirmed={fields.activateSecurityConfirmed}
              instructionsLink={instructionsLink}
              instructionsInnerHtmlToScroll={instructionsInnerHtmlToScrollStep5}
            />
          );
        }

        // Step 6: Configure Auth Policy
        if (currentStep === 6) {
          return (
            <ConfigureAuthPolicy
              authPolicyConfirmed={fields.authPolicyConfirmed}
              instructionsLink={instructionsLink}
              instructionsInnerHtmlToScroll={instructionsInnerHtmlToScrollStep6}
            />
          );
        }

        // Step 7: Activate Auth Policy
        if (currentStep === 7) {
          return (
            <ActivateAuthPolicy
              activateAuthConfirmed={fields.activateAuthConfirmed}
              instructionsLink={instructionsLink}
              instructionsInnerHtmlToScroll={instructionsInnerHtmlToScrollStep7}
            />
          );
        }

        // Step 8: Retrieve Web Services URL
        return (
          <RetrieveWebServicesUrl
            apiBaseUrl={fields.apiBaseUrl}
            instructionsLink={instructionsLink}
            instructionsInnerHtmlToScroll={instructionsInnerHtmlToScrollStep8}
          />
        );
      }}
    />
  );
};

export default SignInWorkdayApiMultiStep;
